// Sugalarm — Cycle 134: month-grid date helpers for the Log calendar.
//
// Pure, DST-safe local-calendar math shared by <LogMonthCalendar> (the
// view) and useMonthTreatments (the fetch). Everything operates on local
// Date objects + `localDayKey` so a cell is the same local calendar day
// the day-stepper and entry rows bucket by — UTC stays on the wire.

export interface MonthGrid {
  year: number;
  /** 0-11. */
  month: number;
  /** Sunday-start, local-midnight cells — 35 (5 weeks) or 42 (6 weeks). */
  cells: Date[];
}

/** Build the Sunday-start grid for a month: the leading days back to the
 *  Sunday on/before the 1st, the month's days, and the trailing days out
 *  to the Saturday on/after the last — 35 or 42 cells. */
export function buildMonthGrid(year: number, month: number): MonthGrid {
  const firstOfMonth = new Date(year, month, 1);
  const leading = firstOfMonth.getDay(); // 0 = Sunday
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells = Math.ceil((leading + daysInMonth) / 7) * 7;
  const cells: Date[] = [];
  for (let i = 0; i < totalCells; i++) {
    // `1 - leading + i` rolls cleanly across month/year boundaries and DST.
    cells.push(new Date(year, month, 1 - leading + i));
  }
  return { year, month, cells };
}

/** Comparable month index (year*12 + month) for floor/ceiling checks. */
export function monthIndex(year: number, month: number): number {
  return year * 12 + month;
}

/** ISO-8601 UTC for `<localDate> 23:59:59.999` local — anchors the
 *  server's `before`-windowed fetch at the end of the given local day
 *  (mirrors the day-stepper's end-of-day anchor; absorbs DST jumps). */
export function endOfLocalDayIso(d: Date): string {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate(),
    23,
    59,
    59,
    999,
  ).toISOString();
}
